import { Box, TextField, type Theme } from "@mui/material";
import { type UseModalState } from "@src/lib/ui-react";
import { useState } from "react";

import { DialogFooter } from "../../components/DialogFooter";
import { InfoBanner } from "../../components/InfoBanner/InfoBanner";
import { ResponsiveDialog } from "../../components/ResponsiveDialog";
import { Text } from "../../components/Text";

interface FcmApprovalDialogProps {
  modalState: UseModalState;
  /**
   * Whether this is updating a previous response (approvalStatus !== -1)
   * If true, reason is required
   */
  isUpdateResponse: boolean;
  isLoading: boolean;
  onApprove: (reason: string) => void;
  onCancel: () => void;
}

/**
 * Modal for approving a FCM (Facility Canceled Me / Sent Home) request.
 * Allows the workplace user to provide an optional reason for approval.
 *
 * When updating a previous response, the reason field becomes required.
 */
export function FcmApprovalDialog(props: FcmApprovalDialogProps) {
  const { modalState, isUpdateResponse, isLoading, onApprove, onCancel } = props;

  const [reason, setReason] = useState("");
  const [hasValidationError, setHasValidationError] = useState(false);

  const isReasonRequired = isUpdateResponse;

  const handleApprove = () => {
    if (isReasonRequired && !reason.trim()) {
      setHasValidationError(true);
      return;
    }
    onApprove(reason);
  };

  const handleCancel = () => {
    setReason("");
    setHasValidationError(false);
    onCancel();
  };

  const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReason(event.target.value);
    if (event.target.value.trim()) {
      setHasValidationError(false);
    }
  };

  return (
    <ResponsiveDialog
      dismissible={false}
      modalState={modalState}
      title="Approve this shift as a sent home?"
      footerContent={
        <DialogFooter
          layout="horizontal"
          primaryAction={{
            label: "Yes, approve",
            onClick: handleApprove,
            isLoading,
          }}
          secondaryAction={{
            label: "No, don't approve",
            onClick: handleCancel,
          }}
        />
      }
    >
      <Box
        sx={(theme: Theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(4),
        })}
      >
        <Text variant="body2">Reason for approval</Text>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder={
            isReasonRequired
              ? "Please provide context for your approval reason"
              : "You may provide context for your approval reason"
          }
          value={reason}
          error={hasValidationError}
          helperText={hasValidationError ? "Please fill out reason for approval." : undefined}
          onChange={handleReasonChange}
        />
        <InfoBanner
          message="Please note this reason will be shared with the worker."
          variant="info"
        />
      </Box>
    </ResponsiveDialog>
  );
}
